/**
 * Once-per-session arrival prompt for AI payee proposals (TMMAPP-0032). Reads
 * `GET /me/review/summary` counts only — never the review list.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { reviewApi } from '@/api/review';
import { useMe } from '@/session/meHooks';
import { useNotify } from '@/shell/NotifyProvider';

/** Module-scoped so remounts within the same SPA session do not re-prompt. */
let arrivalShownThisSession = false;

/** Test-only: reset the once-per-session gate. */
export function resetNamingArrivalForTests(): void {
  arrivalShownThisSession = false;
}

export function NamingArrivalPrompt() {
  const { t } = useTranslation();
  const me = useMe();
  const notify = useNotify();
  const navigate = useNavigate();
  const started = useRef(false);

  useEffect(() => {
    if (me.tier !== 'SUBSCRIBER') return;
    if (arrivalShownThisSession || started.current) return;
    started.current = true;
    let cancelled = false;
    void reviewApi
      .summary()
      .then((summary) => {
        if (cancelled) return;
        const n = summary.counts.AI_PAYEE_PROPOSAL ?? 0;
        if (n > 0) {
          arrivalShownThisSession = true;
          notify(t('naming.arrival', { count: n }), {
            severity: 'info',
            action: {
              label: t('naming.arrivalReview'),
              onClick: () => navigate('/me/payees'),
            },
          });
        }
      })
      .catch(() => {
        /* silent — badge/prompt must not block the shell */
      });
    return () => {
      cancelled = true;
    };
  }, [me.tier, navigate, notify, t]);

  return null;
}
